"use client";

import React from "react";
import { cn } from "@/utilities/core/cn";

export interface PillToggleOption<T extends string = string> {
    /** Unique identifier for the option */
    id: T;
    /** Display label for the option */
    label: string;
}

export interface PillToggleProps<T extends string = string> {
    /** Array of toggle options */
    options: PillToggleOption<T>[];
    /** Currently active option id */
    activeId: T;
    /** Callback when option changes */
    onChange: (id: T) => void;
    /** Optional size variant */
    size?: "sm" | "md";
    /** Optional additional className for the container */
    className?: string;
}

/**
 * PillToggle - A reusable pill-style toggle component for switching between views
 *
 * @example
 * ```tsx
 * <PillToggle
 *   options={[
 *     { id: "fixtures", label: "Fixtures" },
 *     { id: "results", label: "Results" },
 *   ]}
 *   activeId={activeView}
 *   onChange={setActiveView}
 * />
 * ```
 */
export function PillToggle<T extends string = string>({
    options,
    activeId,
    onChange,
    size = "sm",
    className,
}: PillToggleProps<T>) {
    const sizeClasses = {
        sm: "px-4 py-1.5 text-xs",
        md: "px-4 py-1.5 text-sm",
    };

    return (
        <div
            className={cn(
                "overflow-x-auto scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]",
                className
            )}
        >
            <div className="inline-flex gap-1 p-1 bg-muted-foreground/10 rounded-full">
                {options.map((option) => (
                    <button
                        key={option.id}
                        onClick={() => onChange(option.id)}
                        className={cn(
                            "font-medium rounded-full whitespace-nowrap",
                            sizeClasses[size],
                            activeId === option.id ? "tab-active" : "tab-inactive"
                        )}
                    >
                        {option.label}
                    </button>
                ))}
            </div>
        </div>
    );
}
